import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';

import {
  CorporateSummaryData,
  CorporateDetails,
} from '@/api/corporate/corporate.types.ts';
import corporateUserIconGreen from '@/assets/corporateUserIconGreen.png';
import CustomEntityTable, {
  Column,
} from '@/components/controls/CustomEntityTable.tsx';
import CorporateDetailsModal from '@/components/layout/corporateDashboard/CorporateDetailsModal.tsx';
import Pagination from '@/components/layout/Pagination.tsx';
import { useCorporateUsers } from '@/hooks/useCorporateUsers.ts';
import { formatZAR } from '@/lib/currency.ts';

const dashboardColumns: Column<CorporateSummaryData>[] = [
  { key: 'companyName', label: 'Company Name', sortable: true, width: 25 },
  {
    key: 'contactName',
    label: 'Contact Name',
    sortable: true,
    width: 25,
  },
  {
    key: 'createdAt',
    label: 'Created At',
    sortable: true,
    formattingFunc: (value) =>
      dayjs(value as string).format('MMM DD, YYYY [at] HH:mm'),
    width: 20,
  },
  { key: 'referredBy', label: 'Referred By', sortable: false, width: 15 },
  {
    key: 'totalSpend',
    label: 'Total Spend',
    sortable: true,
    formattingFunc: (value) => formatZAR(value),
    width: 15,
  },
];

const CorporateUsersDashboard: FC = () => {
  const {
    corporateUsers,
    corporateCount,
    isFetchingAll,
    pageSize,
    currentPage,
    setCurrentPage,
  } = useCorporateUsers();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCorporate, setSelectedCorporate] =
    useState<CorporateDetails | null>(null);

  const tableData = useMemo(
    () =>
      corporateUsers.map((corporate: CorporateSummaryData) => ({
        ...corporate,
        contactName: `${corporate.name} ${corporate.surname}`,
      })),
    [corporateUsers]
  );

  const handleViewClick = (corporate: CorporateSummaryData): void => {
    setSelectedCorporate(corporate);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = (): void => {
    setSelectedCorporate(null);
    setShowDetailsModal(false);
  };

  return (
    <>
      <CustomEntityTable
        title="Corporate Users"
        columns={dashboardColumns}
        data={tableData}
        totalData={corporateCount}
        iconName={corporateUserIconGreen}
        isLoading={isFetchingAll}
        onView={handleViewClick}
      />
      <Pagination
        totalItems={corporateCount}
        pageSize={pageSize}
        currentPage={currentPage}
        goToPage={setCurrentPage}
      />

      {selectedCorporate ? (
        <CorporateDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          details={selectedCorporate}
        />
      ) : null}
    </>
  );
};

export default CorporateUsersDashboard;
